// Base types
export interface BaseEntity {
  id: string;
  created_at: string;
  updated_at?: string;
}

// User and Authentication
export interface User extends BaseEntity {
  user_id: number;
  username: string;
  email?: string;
  is_active: boolean;
  has_created_project: boolean;
  is_admin?: boolean;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// Project
export interface Project extends BaseEntity {
  project_id: string;
  name: string;
  description: string;
  user_id: string;
  visibility?: string;  // 'private' | 'team' | 'public'
  team_ids?: number[];  // Team IDs when visibility='team'
  is_public?: boolean;  // deprecated: use visibility instead
}

// Property metadata (AC-1251). A property value may be either the legacy
// shorthand string ("int") or a rich object with optional description and
// min/max range. See frontend/src/utils/propertyMeta.ts for helpers.
export interface PropertyMeta {
  type: string;
  description?: string;
  min?: number;
  max?: number;
}

export type PropertyDefinition =
  | string
  | {
      type: string;
      description?: string;
      min?: number;
      max?: number;
    };

// Model
export interface Model extends BaseEntity {
  model_id: number;
  project_id: string;
  class_name: string;
  description: string;
  properties: Record<string, PropertyDefinition>;
  relations?: Record<string, string>;
  // Registry fields (AC-367, AC-372)
  registry_enabled?: boolean;
  registry_max_entries?: number | null;
  registry_dedup_fields?: string[] | null;
}

// Registry-enabled model (subset of Model for tool selection)
export interface RegistryEnabledModel {
  model_id: number;
  class_name: string;
  description: string;
  registry_dedup_fields: string[] | null;
  entry_count: number;
}

// AI Assistant Agent Types
export interface KnowledgeBase extends BaseEntity {
  knowledge_base_id: number;
  user_id: number;
  project_id?: number;
  name: string;
  description?: string;
  content_type: 'project' | 'orchestration' | 'agent' | 'model' | 'general';
  content_id?: number;
  content_text: string;
  content_metadata?: Record<string, any>;
  embedding?: string;
}

export interface AssistantConversation extends BaseEntity {
  conversation_id: number;
  user_id: number;
  project_id?: number;
  title: string;
  context_type: 'project' | 'orchestration' | 'agent' | 'general';
  context_id?: number;
  status: 'active' | 'archived';
}

export interface AssistantMessage extends BaseEntity {
  message_id: number;
  conversation_id: number;
  user_id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  message_type: 'text' | 'code' | 'action';
  message_metadata?: Record<string, any>;
}

export interface AssistantAction extends BaseEntity {
  action_id: number;
  message_id: number;
  // AC-1732 widens the union with the MCP-discovery action types
  // (propose/provision/decline) so the assistant can resolve a capability need
  // to a remote MCP server behind the human-approval gate. The executed
  // provision_mcp_server action surfaces an "Open Integrations" CreatedResource.
  // AC-1784 (epic AC-1778) widens it again with the run-workflow action types
  // (preview/confirm/decline_run_orchestration) so the assistant can RUN the
  // in-context workflow behind a confirm gate. The executed
  // confirm_run_orchestration action carries {orchestration_id, run_instance_id}
  // and drives the in-chat CompactRunProgressCard.
  // AC-1787 (epic AC-1787) widens it with 'configure_schedule' so the journey's
  // FINAL step — the executed schedule confirm — is a typed action. Its result
  // carries {orchestration_id, project_id, orchestration_name} and drives the
  // "Open <workflow>" CreatedResource on the journey-complete turn.
  action_type: 'create_orchestration' | 'modify_orchestration' | 'create_agent' | 'create_project' | 'create_simple_orchestration' | 'explain_code' | 'suggest_improvement' | 'tutorial_step' | 'debug_assistance' | 'propose_mcp_server' | 'provision_mcp_server' | 'decline_mcp_server' | 'preview_run_orchestration' | 'confirm_run_orchestration' | 'decline_run_orchestration' | 'configure_schedule';
  action_data?: Record<string, any>;
  status: 'pending' | 'executed' | 'failed';
  result?: Record<string, any>;
  executed_at?: string;
}

export interface AssistantConversationWithMessages extends AssistantConversation {
  messages: AssistantMessage[];
}

export interface AssistantMessageWithActions extends AssistantMessage {
  actions: AssistantAction[];
}

export interface KnowledgeSearchRequest {
  query: string;
  user_id: number;
  project_id?: number;
  content_types?: string[];
  limit?: number;
  similarity_threshold?: number;
}

export interface KnowledgeSearchResult {
  knowledge_base_id: number;
  name: string;
  content_type: string;
  content_id?: number;
  content_text: string;
  similarity_score: number;
  content_metadata?: Record<string, any>;
}

export interface AssistantChatRequest {
  message: string;
  conversation_id?: number;
  context_type?: 'project' | 'orchestration' | 'agent' | 'general';
  context_id?: number;
  project_id?: number;
  // AC-1767: marks a SYSTEM-DRIVEN turn (the concierge's auto-continue). The
  // backend persists this user turn with message_metadata.system_driven=true so
  // the conversation-load mapper can suppress it on reload — keeping the hidden
  // "continue" driver from ever rendering as a fabricated user bubble.
  system_driven?: boolean;
}

/**
 * A single clarifying question the assistant asks before it can plan a workflow
 * (AC-1644, epic AC-1639). `options` are the suggested answers rendered as
 * one-click chips; `required` marks a question the user cannot skip past.
 */
export interface ClarifyingQuestion {
  field: string;
  question: string;
  options?: string[];
  required?: boolean;
}

/**
 * A preview of the plan the assistant intends to execute, surfaced for explicit
 * user confirmation before any resource is created (AC-1644, epic AC-1639).
 * `agents_to_create` / `queues_to_create` / `models_to_create` name the new
 * resources; `steps` describes the workflow shape; `repairs` lists structural
 * fixes the planner applied (e.g. inserting a missing entry-point step).
 */
export interface PendingActionPreview {
  action_id: number;
  action_type: string;
  step_count: number;
  agents_to_create: string[];
  queues_to_create: string[];
  models_to_create: string[];
  steps: Array<{
    order: number;
    step_type: string;
    intent?: string;
    description?: string;
    agent?: string;
    // AC-1721/AC-1722: per-step agents-as-tools summary for a run_agent step
    // whose orchestrator agent uses other agents as tools.
    agent_as_tools?: Array<{ tool_name: string; child_agent: string }>;
  }>;
  repairs: string[];
  // AC-1721/AC-1722: agents-as-tools edges a create would wire onto a run_agent
  // step's orchestrator agent. Each names the specialist exposed as a tool
  // (child_agent) under tool_name, on the orchestrator. Present on a create
  // preview when a step uses agent_as_tools; empty/absent otherwise.
  agent_tools_to_attach?: Array<{
    tool_name: string;
    child_agent: string;
    orchestrator?: string;
  }>;
  // AC-1657/AC-1659: update-preview fields. Present (alongside
  // action_type === 'preview_update_orchestration') when the assistant is
  // proposing an update to an existing orchestration rather than a create.
  // `summary` is a one-line description of the edit; `changes` is a diff list
  // where each entry names the changed field and carries before/after values.
  summary?: string;
  changes?: Array<Record<string, any>>;
  // AC-1784 (epic AC-1778): run-preview fields. Present (alongside
  // action_type === 'preview_run_orchestration') when the assistant is
  // proposing to EXECUTE the in-context workflow rather than create or update
  // one. `workflow_name` is the orchestration being run; `run_caution` is an
  // always-present "this will execute the workflow (may consume tokens / send
  // email / take time)" note, strengthened when the workflow has externally
  // effectful steps; `input_message` is the OPTIONAL input override echoed back
  // so the user confirms the exact input the run will receive.
  workflow_name?: string;
  run_caution?: string;
  input_message?: string;
}

// Assistant Concierge journey envelope (AC-1756, epic AC-1749).
// Journey position rides additively on the chat/confirm responses; it is purely
// derived response metadata, never persisted client-side (no Zustand, no query).
// NOTE: Journey is declared before JourneyStepView so a source scan for the
// first `interface Journey` token resolves to this interface, not the longer
// `JourneyStepView` name (forward reference is valid in TypeScript).
export interface Journey {
  goal_type: string;
  goal_label: string; // "summarize your emails" (user-facing copy)
  steps: JourneyStepView[];
  complete?: boolean;
  blocked_reason?: string;
}

export interface JourneyStepView {
  kind: string; // 'create_project' | 'configure_builtin' | 'propose_mcp' | 'build_workflow'
  label: string; // id-free, goal-framed ("Create a project", "Connect your email", ...)
  status: 'done' | 'current' | 'remaining' | 'blocked';
}

export interface AssistantChatResponse {
  message_id: number;
  conversation_id?: number;
  content: string;
  actions: AssistantAction[];
  suggested_actions: string[];
  context_used: KnowledgeSearchResult[];
  clarifying_questions?: ClarifyingQuestion[];
  pending_action_preview?: PendingActionPreview | null;
  journey?: Journey;
  // AC-1787 (defect fix): set by the confirm-* apiService wrappers ONLY when the
  // backend answered with a 409 (the pending plan was superseded under the user).
  // A 200 SUCCESS that legitimately carries a NEW pending_action_preview (e.g. the
  // journey's build-confirm surfacing the next-step schedule card) leaves this
  // unset, so the call site can distinguish a real conflict (show the "plan
  // changed" banner) from a clean next-step card (surface it without the banner).
  planChanged?: boolean;
}

// Knowledge Base CRUD
export interface KnowledgeBaseCreate {
  name: string;
  description?: string;
  content_type: 'project' | 'orchestration' | 'agent' | 'model' | 'general';
  content_id?: number;
  content_text: string;
  content_metadata?: Record<string, any>;
  project_id?: number;
}

export interface KnowledgeBaseUpdate {
  name?: string;
  description?: string;
  content_text?: string;
  content_metadata?: Record<string, any>;
}

// Document Upload Types (AC-804)
export type UploadStatus = 'pending' | 'uploading' | 'processing' | 'completed' | 'failed';

export interface UploadFileStatus {
  file: File;
  status: UploadStatus;
  progress: number;
  error?: string;
  knowledgeBaseId?: number;
}

export interface DocumentProcessingResult {
  filename: string;
  success: boolean;
  knowledge_base_ids: number[];
  chunks_created: number;
  error?: string;
  replaced?: boolean;
  chunks_removed?: number;
}

// Deduplication types (AC-818)
export interface DuplicateInfo {
  exists: boolean;
  chunk_count: number;
}

export interface CheckDuplicatesResponse {
  duplicates: Record<string, DuplicateInfo>;
}

export interface DocumentUploadResponse {
  success: boolean;
  results: DocumentProcessingResult[];
  total_processed: number;
  total_failed: number;
}

// Project Documents (AC-830)
export interface ProjectDocumentSummary {
  source_filename: string;
  chunk_count: number;
  uploaded_at: string;
  total_size_chars: number;
}

export interface ProjectDocumentsResponse {
  documents: ProjectDocumentSummary[];
  total_documents: number;
  total_chunks: number;
}

// Assistant Conversation CRUD
export interface AssistantConversationCreate {
  title: string;
  context_type: 'project' | 'orchestration' | 'agent' | 'general';
  context_id?: number;
  status?: 'active' | 'archived';
  project_id?: number;
}

export interface AssistantConversationUpdate {
  title?: string;
  status?: 'active' | 'archived';
}

// Assistant Message CRUD
export interface AssistantMessageCreate {
  conversation_id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  message_type?: 'text' | 'code' | 'action';
  message_metadata?: Record<string, any>;
}

export interface AssistantMessageUpdate {
  content?: string;
  message_type?: 'text' | 'code' | 'action';
  message_metadata?: Record<string, any>;
}

// Assistant Action CRUD
export interface AssistantActionCreate {
  message_id: number;
  action_type: 'create_orchestration' | 'modify_orchestration' | 'create_agent' | 'create_project' | 'create_simple_orchestration' | 'explain_code' | 'suggest_improvement' | 'tutorial_step' | 'debug_assistance';
  action_data?: Record<string, any>;
  status?: 'pending' | 'executed' | 'failed';
  result?: Record<string, any>;
}

export interface AssistantActionUpdate {
  action_data?: Record<string, any>;
  status?: 'pending' | 'executed' | 'failed';
  result?: Record<string, any>;
  executed_at?: string;
}

export interface PropertySchema {
  type: string;
  literalValues?: string[]; // Changed from enum to literalValues
  description?: string;
  format?: string;
}

// Agent
export type OutputType = 'str' | 'model';

export type RegistryUseCase = 'deduplicate' | 'track' | 'log' | 'full';

// Which call shape the agent receives for registry tools (when use case is not 'full').
// 'batch' is the default — exposing only one variant prevents the agent from
// burning tokens choosing between near-identical batch and single-item tools.
export type RegistryCallStyle = 'batch' | 'single';

export type KnowledgeContentType = 'project' | 'orchestration' | 'agent' | 'model' | 'general' | 'document';

export interface KnowledgeSearchToolConfig {
  content_types?: KnowledgeContentType[];
  result_limit?: number;
  similarity_threshold?: number;
}

export interface AgentTool {
  type: 'agent' | 'web_search' | 'function' | 'godaddy_check_availability' | 'godaddy_search_domains' | 'godaddy_get_pricing' | 'godaddy_validate_purchase' | 'godaddy_purchase_domain' | 'godaddy_configure_purchase_rules' | 'rdap_check_availability' | 'registry' | 'mcp' | 'knowledge_search';
  name?: string;
  description?: string;
  agent_as_a_tool_id?: string;
  registry_model_id?: number;  // For registry-enabled model tools
  registry_use_case?: RegistryUseCase;  // Use case for registry tools
  registry_call_style?: RegistryCallStyle;  // 'batch' (default) or 'single'; ignored when use case is 'full'
  mcp_server_id?: number;  // For MCP tools - references the MCP server
  mcp_tool_name?: string;  // For MCP tools - the tool name on the server
  tool_config?: KnowledgeSearchToolConfig | { credential_id: number };  // For knowledge_search or MCP credential tools
  user_location?: {
    type: 'approximate';
    city: string;
    region: string;
    country: string;
  };
}

export interface Agent extends BaseEntity {
  agent_id: number;
  project_id: string;
  name: string;
  instructions: string;
  hints?: string;
  model?: string;
  model_settings?: Record<string, any>;
  output_type?: OutputType;
  output_model_id?: number | null;
  output_model?: Model;
  tools?: AgentTool[];
}

// Workflow
export type StepType = 'run_agent' | 'user_prompt' | 'conditional' | 'auto_prompt' | 'queue_write' | 'queue_read' | 'filter' | 'rdap_lookup';
export type StepIntent = 'create' | 'enhance' | 'validate' | 'conditional' | 'assistant';
export type InputType = 'input_message' | 'final_output' | 'input_list' | 'model';

// Step type display names
export const STEP_TYPE_DISPLAY_NAMES: Record<string, string> = {
  'run_agent': 'Agent',
  'user_prompt': 'User Prompt',
  'conditional': 'Conditional',
  'auto_prompt': 'Auto Prompt',
  'queue_write': 'Queue Write',
  'queue_read': 'Queue Read',
  'filter': 'Filter',
  'rdap_lookup': 'Domain Availability'
};

/**
 * Config schema for a `filter` step (AC-1273). Stored in
 * ``orchestration_step.config`` JSONB.
 *
 *  - `output_field` defaults to `source_field` when omitted.
 *  - `on_empty='continue'` keeps the run going on zero kept elements;
 *    `'exit'` terminates the run.
 *  - `element_class_name` is an optional override for element-type resolution
 *    (usually set only when the parent model's `list[X]` annotation is
 *    ambiguous).
 */
export interface FilterConfig {
  source_field: string;
  output_field: string;
  conditions: string[];
  on_empty: 'continue' | 'exit';
  element_class_name?: string;
  // AC-1358: registry-aware filter mode. Optional fields keep existing
  // persisted predicate-mode configs type-compatible.
  mode?: 'predicate' | 'registry_dedup';
  registry_model_id?: number | null;
  register_kept?: boolean;
}

// LEGACY: Old display names (for backward compatibility only)
export const INTENT_TO_DISPLAY_NAME: Record<StepIntent, string> = {
  'create': 'Generator',
  'enhance': 'Extender',
  'validate': 'Validator',
  'conditional': 'Conditional',
  'assistant': 'Assistant'
};

// PREFERRED: Intent-based action text for modern UI
export const INTENT_ACTION_TEXT: Record<StepIntent, string> = {
  'create': 'Create Content',
  'enhance': 'Enhance Content',
  'validate': 'Validate Content',
  'conditional': 'Apply Logic',
  'assistant': 'Assist User'
};

/**
 * Gets user-friendly display name for a step (legacy compatibility)
 */
export function getStepDisplayName(step: WorkflowStep): string {
  if (step.step_type === 'run_agent' && step.intent) {
    return INTENT_TO_DISPLAY_NAME[step.intent] || 'Agent';
  }
  return STEP_TYPE_DISPLAY_NAMES[step.step_type] || step.step_type;
}

/**
 * Gets intent-based action text for modern UI (PREFERRED)
 */
export function getIntentActionText(intent?: StepIntent): string {
  return intent ? INTENT_ACTION_TEXT[intent] : 'Execute Task';
}

/**
 * Gets short intent display text for badges
 */
export function getIntentDisplayText(intent?: StepIntent): string {
  if (!intent) return 'Task';
  
  switch (intent) {
    case 'create': return 'Create';
    case 'enhance': return 'Enhance';
    case 'validate': return 'Validate';
    case 'conditional': return 'Logic';
    case 'assistant': return 'Assist';
    default: return intent;
  }
}

/**
 * Gets the appropriate step type to send to API
 */
export function getApiStepType(displayType: string): string {
  switch (displayType) {
    case 'Generator': return 'run_agent'; // Will need intent='create'
    case 'Extender': return 'run_agent'; // Will need intent='enhance'
    case 'Validator': return 'run_agent'; // Will need intent='validate'
    case 'Assistant': return 'run_agent'; // Will need intent='assistant'
    case 'Agent': return 'run_agent';
    default: return displayType.toLowerCase().replace(' ', '_');
  }
}

/**
 * Gets the intent for new agent steps based on display type
 */
export function getStepIntent(displayType: string): StepIntent | undefined {
  switch (displayType) {
    case 'Generator': return 'create';
    case 'Extender': return 'enhance';
    case 'Validator': return 'validate';
    case 'Assistant': return 'assistant';
    default: return undefined;
  }
}

export interface ConditionalAction {
  action: 'continue' | 'exit' | 'feedback_loop';
  next_step_id?: string;
  feedback?: string;
  score_variable?: string;
  max_attempts?: number;
}

export interface SessionConfig {
  enabled: boolean;
  mode: 'isolated' | 'shared' | 'run';
  type: 'conversation' | 'workflow';
  ttl_minutes?: number;
  max_messages?: number;
}

export interface AssistantConfig {
  conversation_mode?: 'stateful' | 'stateless';
  max_turns?: number;
  auto_loop?: boolean;
  context_window?: number;
  ui_mode?: 'chat' | 'hybrid';
  session_persistence?: boolean;
  tools_enabled?: string[];
  greeting_message?: string;
  session_ttl?: number;
}


// Intent-aware step type and intent combinations for validation
export interface AllowedStepIntentOption {
  step_type: StepType;
  intent: StepIntent | null;
}

export interface AllowedIntentsRequest {
  prev_step_type?: StepType;
  prev_intent?: StepIntent;
  orchestration_id?: number;
}

export interface WorkflowStep extends BaseEntity {
  orchestration_step_id: string;
  orchestration_id: string;
  lineage_id?: string;
  step_type: StepType;
  intent?: StepIntent;
  step_order: number;
  agent_id?: number;
  /**
   * AC-1539 / AC-1548 (epic AC-1537): step-level model identifier (e.g.
   * 'gpt-4o-mini', 'claude-haiku-4-5'). When set this determines which
   * runtime adapter executes the step and overrides ``agent.model`` for
   * runtime selection. When ``null`` / ``undefined`` the backend falls
   * back to ``agent.model`` and then to the app config default. Only
   * meaningful for ``step_type === 'run_agent'``.
   */
  model?: string | null;
  input_message?: string;
  description?: string;
  label?: string;
  show_output_message_items?: boolean;
  condition?: {
    conditions: Condition[];
  };
  on_true?: ConditionalAction;
  on_false?: ConditionalAction;
  previous_step_id?: string;
  position_x?: number;
  position_y?: number;
  execution_mode?: 'synchronous' | 'parallel';
  fan_out?: boolean;
  hints?: Record<string, any>;
  feedback_loop_hints?: Record<string, any>;
  session_config?: SessionConfig;
  assistant_config?: AssistantConfig;
  auto_prompt_output_mode?: 'text' | 'model';
  auto_prompt_model_id?: number | null;
  auto_prompt_populated_data?: Record<string, any>;
  /** Step-specific configuration (e.g., for queue_read, queue_write steps) */
  config?: Record<string, any>;
  /** Skip version control recording for position-only updates */
  skip_version_control?: boolean;
}

export interface Condition {
  field: string;
  operator: string;
  value: any;
}

export interface Workflow extends BaseEntity {
  orchestration_id: string | number;
  project_id: string;
  name: string;
  description: string;
  auto_summarize: boolean;
  summary_model?: string;
  status?: 'running' | 'completed' | 'failed' | 'stopped';
  steps?: WorkflowStep[];
  start_node_position_x?: number;
  start_node_position_y?: number;
  /**
   * AC-1816 (epic AC-1813). Optional per-workflow delivery setting. On terminal
   * success the runner delivers the rendered result to `delivery_destination`
   * over `delivery_channel`, reusing the existing built-in credential. NULL on
   * all three = no delivery (today's behavior). Mirrors `auto_summarize`: plain
   * columns persisted via `updateWorkflow`.
   */
  delivery_channel?: DeliveryChannel | null;
  delivery_destination?: string | null;
  delivery_credential_id?: number | null;
}

/**
 * AC-1816 (epic AC-1813). The delivery channels a workflow result can be sent to.
 * Mirrors the backend `ck_delivery_channel_valid` CHECK constraint. NULL channel
 * (absence) means no delivery.
 */
export type DeliveryChannel = 'whatsapp' | 'slack' | 'email';

/**
 * AC-1816 (epic AC-1813). One entry per delivery channel describing whether the
 * current user can use it (the channel's built-in is connected). Sourced from
 * `GET /api/v1/delivery/channels` (apiService.getDeliveryChannelAvailability).
 * Only channels with `available: true` are offered for selection; unavailable
 * channels are disabled and surface `reason` plus a setup hint.
 */
export interface ChannelAvailability {
  channel: DeliveryChannel;
  /** True when the channel's built-in has a usable credential connected. */
  available: boolean;
  /** Why the channel is unavailable (e.g. "Slack not connected"), else null. */
  reason: string | null;
  /**
   * The connected built-in credentials/instances for this channel (Slack/Email
   * may have several). Empty for WhatsApp (single connected number, no choice).
   */
  credentials?: Array<{ credential_id: number; label: string }>;
}

/**
 * AC-1816 (epic AC-1813). Response shape of `GET /api/v1/delivery/channels`.
 */
export interface DeliveryChannelAvailabilityResponse {
  channels: ChannelAvailability[];
}

// Run Instance and Results
export type RunStatus = 'pending' | 'running' | 'completed' | 'failed' | 'waiting_for_input' | 'stopped' | 'paused';

export interface RunInstance extends BaseEntity {
  run_instance_id: string;
  orchestration_id: string;
  status: RunStatus;
  summary?: string;
}

// Classification keys persisted on run_result.error_metadata.classification.
//
// Legacy (AC-1346): 'timeout' | 'max_turns' | 'other' -- feed the
//   underpowered-model callout when suggested_model is non-null.
// AC-1533: 'insufficient_credits' | 'invalid_api_key' | 'rate_limited' |
//   'model_not_found' | 'context_length_exceeded' -- feed the friendly
//   provider-error callout when user_message is present.
export type ErrorClassification =
  | 'timeout'
  | 'max_turns'
  | 'insufficient_credits'
  | 'invalid_api_key'
  | 'rate_limited'
  | 'model_not_found'
  | 'context_length_exceeded'
  | 'other';

// Friendly metadata attached when classify_agent_error returns one of the
// AC-1533 provider classifications. The backend lifts these off the
// raised RuntimeModelError subclass; the frontend renders them as a
// callout in RunResultErrorPanel.
export type ErrorActionUrl =
  | string  // http(s) external, internal /route, or 'edit-agent://...' sentinel
  | undefined;

export interface ErrorMetadata {
  classification: ErrorClassification;
  model: string;
  tier: 'light' | 'standard' | 'premium' | 'reasoning';
  agent_id: number;
  agent_name: string;
  is_complex: boolean;
  complexity_signals: string[];
  complexity_score: number;
  suggested_model: string | null;
  // AC-1533: user-facing fields populated for provider error classifications.
  // Each is optional so the legacy AC-1346 metadata shape remains valid.
  user_message?: string;
  action_label?: string;
  action_url?: ErrorActionUrl;
}

export interface RunResult extends BaseEntity {
  run_result_id: string;
  run_instance_id: string;
  orchestration_step_id: string;
  step_order: number;
  status: RunStatus;
  input_step_id?: string;
  input_run_result_id?: string;
  input_type?: InputType;
  input_data?: any;
  final_output?: string;
  error_message?: string;
  error_metadata?: ErrorMetadata;
  started_at: string;
  completed_at?: string;
  // Add these fields to match backend API and fix linter errors
  input_message?: string;
  step_type?: string; // Add step_type field from orchestration_step
  session_id?: number; // Session ID linking to agent session
  as_input_list_items?: { item_data: any; item_index: number }[];
  hint_application_details?: {
    iteration?: number;
    previous_feedback?: string;
    previous_score?: string | number | boolean;
    applied_hints?: Array<{
      hint_type: string;
      value: string;
    }>;
    actual_instructions?: string;  // The actual instructions used (including any enhancements)
  };
  image_attachments?: Array<{
    data: string;       // Base64-encoded image data
    mimeType: string;   // e.g. "image/png"
    tool_name?: string; // e.g. "browser_screenshot"
    server_name?: string;
  }>;
}

// Session Message types
export interface SessionMessage {
  message_id: number;
  session_id: number;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  content_json?: any;
  tool_calls?: any;
  message_metadata?: any;
  message_order: number;
  created_at: string;
  run_instance_id?: string;
}

// Session Info types
export interface SessionInfo {
  session_id: number;
  session_key: string;
  session_type: 'conversation' | 'workflow' | 'shared';
  message_count: number;
  created_at: string;
  updated_at?: string;
  expires_at?: string;
  is_active: boolean;
}

// Session Clear types
export interface ClearSessionRequest {
  confirm: boolean;
  clear_mode?: 'soft' | 'hard';
  reason?: string;
  stop_orchestration?: boolean;
}

export interface ClearSessionResponse {
  success: boolean;
  session_keys_cleared: string[];
  messages_deleted: number;
  sessions_marked_inactive: number;
  orchestration_stopped: boolean;
  cleared_at: string;
  message: string;
}

// Enhanced Run Results Response
export interface RunResultsWithSession {
  results: RunResult[];
  session_messages: SessionMessage[];
  session_info_map: Record<number, SessionInfo>;
  run_instance_id: number;
  orchestration_id: number;
  status: string;
  created_at: string;
  updated_at: string;
}

// React Flow types
export interface NodeData {
  id: string;
  label: string;
  type: StepType;
  agent?: Agent;
  step?: WorkflowStep;
  status?: RunStatus;
  isEditing?: boolean;
  isSelected?: boolean;
  onStartExecution?: () => void;
}

export interface EdgeData {
  condition?: string;
  isConditional?: boolean;
  label?: string;
}

// UI State types
export interface FlowState {
  nodes: any[];
  edges: any[];
  selectedNode: string | null;
  selectedEdge: string | null;
  isValidating: boolean;
  validationErrors: string[];
  zoom: number;
  viewport: { x: number; y: number; zoom: number };
}

export interface UIState {
  sidebarOpen: boolean;
  propertiesPanelOpen: boolean;
  componentLibraryOpen: boolean;
  testingPanelOpen: boolean;
  currentView: 'orchestration' | 'models' | 'agents' | 'testing' | 'orchestrations';
  selectedProject: Project | null;
  isLoading: boolean;
  error: string | null;
}

// Component Library
export interface ComponentTemplate {
  id: string;
  name: string;
  description: string;
  type: StepType;
  category: 'input' | 'processing' | 'output' | 'control' | 'custom';
  icon: string;
  defaultConfig: Partial<WorkflowStep>;
  requiredFields: string[];
}

// Testing
export interface TestCase {
  id: string;
  name: string;
  description: string;
  input: any;
  expectedOutput: any;
  status: 'pending' | 'running' | 'passed' | 'failed';
  actualOutput?: any;
  error?: string;
}

export interface TestSuite {
  id: string;
  name: string;
  workflowId: string;
  testCases: TestCase[];
  status: 'pending' | 'running' | 'completed';
  results?: {
    passed: number;
    failed: number;
    total: number;
  };
}

// Search and Filter
export interface SearchFilters {
  query: string;
  type?: StepType | 'all';
  status?: RunStatus | 'all';
  agent?: string;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

// Import/Export
export interface WorkflowTemplate {
  name: string;
  description: string;
  version: string;
  orchestration: Omit<Workflow, 'id' | 'project_id' | 'created_at' | 'updated_at'>;
  agents: Omit<Agent, 'id' | 'project_id' | 'created_at' | 'updated_at'>[];
  models: Omit<Model, 'id' | 'project_id' | 'created_at' | 'updated_at'>[];
  metadata: {
    exportedAt: string;
    exportedBy: string;
    tags: string[];
  };
}

// API Response types
export interface ApiResponse<T> {
  data: T;
  message?: string;
  status: 'success' | 'error';
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Error types
export interface ApiError {
  message: string;
  code?: string;
  details?: any;
}

// Form types
export interface CreateProjectForm {
  name: string;
  description: string;
}

export interface CreateAgentForm {
  name: string;
  instructions: string;
  model?: string;
  model_settings?: Record<string, any>;
  output_type?: OutputType;
  output_model_id?: number | null;
  tools?: AgentTool[];
}

export interface CreateModelForm {
  class_name: string;
  description: string;
  properties: Record<string, string>;
}

export interface CreateWorkflowForm {
  name: string;
  description: string;
  start_node_position_x?: number;
  start_node_position_y?: number;
  skip_version_control?: boolean; // Skip version control recording for position-only updates
  auto_summarize?: boolean; // Enable auto-summarize for workflow runs
  // AC-1816 (epic AC-1813): per-workflow delivery setting. Persisted via
  // updateWorkflow alongside skip_version_control, exactly like auto_summarize.
  delivery_channel?: DeliveryChannel | null;
  delivery_destination?: string | null;
  delivery_credential_id?: number | null;
}

// Validation types
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export interface ValidationError {
  id: string;
  type: 'circular_dependency' | 'missing_connection' | 'invalid_condition' | 'missing_required_field';
  message: string;
  nodeId?: string;
  edgeId?: string;
  severity: 'error' | 'warning';
}

export interface ValidationWarning {
  id: string;
  type: 'unreachable_node' | 'unused_agent' | 'performance_concern';
  message: string;
  nodeId?: string;
  severity: 'warning' | 'info';
}

// Undo/Redo types
export interface HistoryState {
  past: FlowState[];
  present: FlowState;
  future: FlowState[];
  canUndo: boolean;
  canRedo: boolean;
}

// Preferences
export interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  autoSave: boolean;
  autoValidate: boolean;
  showMinimap: boolean;
  showGrid: boolean;
  snapToGrid: boolean;
  gridSize: number;
  defaultZoom: number;
  panOnDrag: boolean;
  zoomOnScroll: boolean;
  multiSelectionKeyCode: 'ctrlCmd' | 'shift' | 'alt';
}

// WebSocket types for real-time updates
export interface WebSocketMessage {
  type: 'run_status_update' | 'orchestration_update' | 'validation_result';
  payload: any;
  timestamp: string;
}

export interface RunStatusUpdate {
  runInstanceId: string;
  status: RunStatus;
  stepOrder?: number;
  output?: string;
  error?: string;
}

// GoDaddy Integration Types
export interface GoDaddyCredentialsCreate {
  api_key: string;
  api_secret: string;
  environment: 'production' | 'ote';
}

export interface GoDaddyCredentialsStatus {
  configured: boolean;
  environment?: 'production' | 'ote';
  encrypted: boolean;
  last_updated?: string;
  api_key_preview?: string;
}

export interface GoDaddyTestConnectionRequest {
  api_key?: string;
  api_secret?: string;
  environment?: 'production' | 'ote';
  use_saved_credentials?: boolean;
}

export interface GoDaddyTestConnectionResponse {
  success: boolean;
  message: string;
  environment: string;
}

// Agent Template Types
export type AgentTemplateCategory =
  | 'web_scraping'
  | 'data_analysis'
  | 'api_integration'
  | 'content_generation'
  | 'domain_research'
  | 'domain_purchase'
  | 'general';

export interface AgentTemplate extends BaseEntity {
  template_id: number;
  name: string;
  description?: string;
  category: AgentTemplateCategory;
  instructions: string;
  hints?: string;
  model: string;
  model_settings?: Record<string, any>;
  output_type: string;
  tool_configurations?: Array<{
    type: string;
    name?: string;
    description?: string;
    mcp_server_id?: number;
    mcp_tool_name?: string;
  }>;
  tags?: string[];
  sample_inputs?: string[];
  sample_outputs?: string[];
  is_active: boolean;
}

export interface AgentTemplateList {
  templates: AgentTemplate[];
  total: number;
}

export interface InstantiateTemplateRequest {
  project_id: number;
  name?: string;
  instructions_override?: string;
}

// GoDaddy Purchase Rules Types
export interface PurchaseRules {
  autonomous_enabled?: boolean;
  daily_budget?: number;
  monthly_budget?: number;
  auto_approve_under?: number;
  require_approval_over?: number;
  allowed_tlds?: string[];
  max_purchases_per_hour?: number;
  emergency_stop?: boolean;
}

export interface PurchaseRulesResponse {
  purchase_rules: PurchaseRules | null;
}

// GoDaddy Purchase History Types
export interface PurchaseContext {
  agent_name?: string;
  agent_id?: number;
  run_instance_id?: string;
  orchestration_id?: string;
  orchestration_name?: string;
}

export interface DomainPurchaseRecord {
  purchase_id: number;
  domain: string;
  price: number;
  currency: string;
  status: 'completed' | 'pending' | 'failed' | 'refunded';
  purchased_at: string;
  purchased_by: 'user' | 'agent';
  purchase_context: PurchaseContext;
  order_id?: string;
  confirmation_code?: string;
  years: number;
  auto_renew: boolean;
  privacy: boolean;
  project_id: number;
}

export interface PurchaseHistoryFilters {
  date_from?: string;
  date_to?: string;
  status?: 'completed' | 'pending' | 'failed' | 'refunded';
  purchased_by?: 'user' | 'agent';
  domain_search?: string;
  min_price?: number;
  max_price?: number;
}

export interface PurchaseHistoryResponse {
  purchases: DomainPurchaseRecord[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface PurchaseHistoryProps {
  projectId: number;
  onViewDetails?: (purchaseId: number) => void;
}

// Model Registry Types
export interface RegistryConfigResponse {
  registry_enabled: boolean;
  registry_dedup_fields: string[] | null;
}

export interface RegistryConfigUpdate {
  registry_enabled?: boolean;
  registry_dedup_fields?: string[] | null;
}

export interface RegistryEntryResponse {
  id: string;
  project_id: number;
  model_id: number;
  output_value: Record<string, any>;
  dedup_key_hash: string;
  dedup_key_value: Record<string, any>;
  source_agent_id: number | null;
  source_agent_name: string | null;
  source_run_instance_id: number | null;
  created_at: string;
  expires_at: string | null;
}

export interface RegistryEntriesListResponse {
  entries: RegistryEntryResponse[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface RegistryEntriesParams {
  page?: number;
  page_size?: number;
  search?: string;
  source_agent_id?: number;
  expires_before?: string;
  expires_after?: string;
  created_after?: string;
  created_before?: string;
  expires_within_days?: number;
  sort_by?: 'created_at' | 'expires_at' | 'source_agent_id' | 'dedup_key';
  sort_direction?: 'asc' | 'desc';
}

export interface AgentEntryCount {
  agent_id: number;
  agent_name: string;
  count: number;
}

export interface RegistryStatsResponse {
  total_entries: number;
  expiring_soon: number;
  added_this_week: number;
  entries_by_agent: AgentEntryCount[];
  // AC-372: Timeline and storage fields for statistics dashboard
  oldest_entry_date?: string | null;  // ISO timestamp of oldest entry
  newest_entry_date?: string | null;  // ISO timestamp of newest entry
  storage_usage_percent?: number | null;  // (total / max_entries) * 100, null if unlimited
}

// AC-399: Circuit Breaker Types
export interface CircuitBreakerState {
  state: 'CLOSED' | 'OPEN';
  enabled: boolean;
  opened_at: string | Date | null;
  reason: string | null;
}

export interface CircuitBreakerSettings {
  circuit_breaker_enabled: boolean;
  failure_rate_threshold: number;
  consecutive_failures_threshold: number;
  auto_reset_enabled: boolean;
  auto_reset_cooldown_minutes: number;
}

// Queue update settings - combines name, description, circuit breaker and consumer mode
export interface QueueUpdateSettings extends Partial<CircuitBreakerSettings> {
  name?: string;
  description?: string;
  single_consumer_mode?: boolean;
  /** Top-level payload field name used as priority for priority queues. Empty string clears it. */
  priority_field?: string | null;
  /** AC-1418: Operator-level enable/disable toggle that pauses auto-trigger
   * without modifying the trigger configuration. */
  is_enabled?: boolean;
  /** AC-1479: Per-queue list of payload dot-paths to project as columns. */
  display_payload_fields?: string[];
}

export interface CircuitTripEvent {
  queue_id: number;
  queue_name: string;
  reason: string;
  last_error_message?: string;
  opened_at: string | Date;
}

// AC-1478: Priority histogram types. The frontend hook is contract-bound to
// what the AC-1477 backend endpoint actually emits, which uses the names
// `status_filter`, `bin_strategy`, and `bins[].label` (verified in
// `test/api/test_queue_priority_histogram.py`). The Technical Design uses
// older names (`status`, `mode`) — the live server response is authoritative.
export type HistogramStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'FAILED'
  | 'COMPLETED'
  | 'ALL';

export type BinStrategy = 'distinct' | 'range';

export interface PriorityHistogramBin {
  /** Lower bound of the bin (inclusive). For distinct mode, lower === upper. */
  lower: number;
  /** Upper bound of the bin. Distinct: same as lower. Range: open upper bound except final bin. */
  upper: number;
  /** Number of items in this bin. */
  count: number;
  /** Server-rendered label for the x-axis tick (e.g. "p=5" or "[10, 20)"). */
  label: string;
}

export interface PriorityHistogramResponse {
  queue_id: number;
  /** Echoes the requested status filter (or "ALL"). */
  status_filter: HistogramStatus;
  /** Total items matching the filter. */
  total: number;
  /** Min priority across the filtered set. Null when total === 0. */
  min: number | null;
  /** Max priority across the filtered set. Null when total === 0. */
  max: number | null;
  /** COUNT(DISTINCT priority) across the filtered set. */
  distinct_count: number;
  /** Whether the server returned per-distinct-value bars or fixed-width range bars. */
  bin_strategy: BinStrategy;
  bins: PriorityHistogramBin[];
}

// AC-1479: Payload-field discovery types. The frontend hook is contract-bound
// to what the AC-1477 backend endpoint emits, which uses
// `path`, `count`, `frequency`, `example` per field plus the response-level
// `queue_id`, `total_items`, `sample_size`, `skipped_oversize`. Verified in
// `test/api/test_queue_payload_fields.py`.
export interface PayloadFieldStat {
  /** Dot-path of the discovered field, e.g. "domain" or "task.id". */
  path: string;
  /** Number of sampled items that contained this path. */
  count: number;
  /** Fraction of sampled items containing this path (0..1). */
  frequency: number;
  /** Stringified sample value, or null when no example is available. */
  example: string | null;
}

export interface PayloadFieldsResponse {
  queue_id: number;
  /** Number of items the server actually inspected (clamped to <= sample). */
  sample_size: number;
  /** Total items available in the queue (so callers can show "x of N"). */
  total_items: number;
  /** Items skipped because their payload exceeded the 64 KB inspection cap. */
  skipped_oversize: number;
  fields: PayloadFieldStat[];
}

// AC-416: Queue Type
export interface Queue {
  queue_id: number;
  project_id: string;
  name: string;
  description: string;
  // AC-460: Data model and configuration fields
  data_model_id: number;
  max_retry_count: number;
  is_error_queue: boolean;
  parent_queue_id?: number | null;
  // AC-1315: populated by the list/get endpoint for primary queues so the
  // Queues list can show an "N errors" pill without a second round-trip.
  error_queue_id?: number | null;
  error_queue_item_count?: number | null;
  // AC-402: Consumer mode
  single_consumer_mode: boolean;
  // Circuit breaker configuration
  circuit_breaker_enabled: boolean;
  failure_rate_threshold: number;
  consecutive_failures_threshold: number;
  auto_reset_enabled: boolean;
  auto_reset_cooldown_minutes: number;
  circuit_breaker_state: 'CLOSED' | 'OPEN';
  circuit_opened_at?: string | Date | null;
  circuit_open_reason?: string | null;
  created_at: string | Date;
  updated_at?: string | Date;
  // AC-1418: Operator-level enable/disable toggle. When false, the
  // scheduler skips this queue regardless of trigger configuration.
  is_enabled?: boolean;
  // AC-413: Scheduling fields
  auto_trigger_enabled?: boolean;
  trigger_interval_minutes?: number | null;
  trigger_item_threshold?: number | null;
  trigger_age_threshold_minutes?: number | null;
  skip_if_empty?: boolean;
  trigger_condition_mode?: 'AND' | 'OR';
  last_triggered_at?: string | Date | null;
  next_trigger_at?: string | Date | null;
  /** Top-level payload field name used as priority. When set, items are pulled in priority DESC, created_at ASC order. */
  priority_field?: string | null;
  // AC-1261: Drain mode - when enabled, a single trigger loops the consumer
  // workflow until the queue is empty or a safety limit is reached.
  drain_mode?: boolean;
  drain_max_cycles?: number;
  // AC-1303: per-queue retention for COMPLETED items. Null means keep forever;
  // a positive integer N means the scheduler deletes COMPLETED items whose
  // updated_at is older than N days.
  completed_retention_days?: number | null;
  // AC-1319: optional cap on per-item lifetime error-queue trips. Null =
  // unlimited operator retries; a positive integer N means manual retry
  // refuses once the item has hit the error queue that many times.
  max_total_failures?: number | null;
  // AC-1479: Per-queue list of payload dot-paths to project as columns in
  // the QueueItemsList. Empty/undefined falls back to the truncated-JSON
  // catch-all column. Server-side validation caps this at 20 entries; the
  // UI uses a tighter cap of 8.
  display_payload_fields?: string[];
}

/**
 * AC-413: Queue Scheduling Settings
 *
 * Note: last_triggered_at and next_trigger_at are intentionally excluded
 * as they are read-only backend fields calculated during queue updates.
 */
export interface SchedulingSettings {
  auto_trigger_enabled: boolean;
  trigger_interval_minutes: number | null;
  trigger_item_threshold: number | null;
  trigger_age_threshold_minutes: number | null;
  skip_if_empty: boolean;
  trigger_condition_mode: 'AND' | 'OR';
  // AC-1261: Drain mode configuration
  drain_mode?: boolean;
  drain_max_cycles?: number;
  // AC-1303: per-queue retention for COMPLETED items (days); null = keep forever.
  completed_retention_days?: number | null;
  // AC-1319: per-queue cap on per-item lifetime error-queue trips; null = unlimited.
  max_total_failures?: number | null;
}

// Queue Consumer Types (AC-402)
export type ConsumerRole = 'PRIMARY' | 'PARALLEL' | 'BACKUP';

export interface QueueConsumer {
  queue_id: number;
  orchestration_id: number;
  orchestration_name?: string;
  consumer_role: ConsumerRole;
  is_active: boolean;
  last_heartbeat_at?: string | Date | null;
  registered_at?: string | Date | null;
}

export interface ConsumerRegisterRequest {
  orchestration_id: number;
  consumer_role: ConsumerRole;
}

// AC-472: Configured consumers (from step configs, not active registrations)
export interface QueueConsumerConfig {
  orchestration_id: number;
  orchestration_name: string;
  consumer_role: ConsumerRole;
  step_id: number;
}

// AC-435: Queue Item Types
export type QueueItemStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface QueueItem {
  item_id: string;
  queue_id: number;
  payload: Record<string, any>;
  status: QueueItemStatus;
  priority: number;
  retry_count: number;
  // AC-1319: monotonic lifetime counter of error-queue trips. Distinct from
  // retry_count, which resets to 0 on operator-triggered manual retry.
  total_failure_count?: number;
  max_retries: number;
  error_message?: string | null;
  created_at: string | Date;
  updated_at?: string | Date;
  processed_at?: string | Date | null;
}

export interface QueueStats {
  total: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}

// AC-450: Schedule Types
export interface Schedule {
  schedule_id: number;
  orchestration_id: number;
  schedule_type: 'interval' | 'cron' | 'one_time';
  interval_minutes: number | null;
  cron_expression: string | null;
  timezone: string | null;
  is_enabled: boolean;
  next_run_at: string | null;
  last_run_at: string | null;
  last_run_status: 'completed' | 'failed' | 'skipped' | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ScheduleCreate {
  orchestration_id: number;
  schedule_type: string;
  interval_minutes?: number;
  cron_expression?: string;
  timezone?: string;
  is_enabled?: boolean;
}

export interface ScheduleUpdate {
  schedule_type?: string;
  interval_minutes?: number;
  cron_expression?: string;
  timezone?: string;
  is_enabled?: boolean;
}

export interface SchedulePreviewRun {
  utc: string;
  local: string;
  timezone: string;
}

// MCP (Model Context Protocol) Types
// AC-1738 (epic AC-1732): 'streamable-http' added so assistant-provisioned
// remote servers (current MCP transport spec) round-trip through the same types
// as hand-added stdio/sse servers.
export type MCPTransportType = 'stdio' | 'sse' | 'streamable-http';
export type MCPHealthStatus = 'connected' | 'disconnected' | 'error' | 'unknown';

export interface MCPServerStdioConfig {
  command: string;
  args: string[];
  cwd?: string;
}

export interface MCPServerSSEConfig {
  url: string;
  headers?: Record<string, string>;
}

/**
 * AC-1738: transport config for a Streamable HTTP MCP server (single POST
 * endpoint that answers JSON or an SSE frame). Mirrors `MCPServerSSEConfig`;
 * headers carrying secrets are injected from encrypted env vars at connection
 * time and are not persisted in plaintext here.
 */
export interface MCPServerStreamableHttpConfig {
  url: string;
  headers?: Record<string, string>;
}

export interface MCPToolInfo {
  name: string;
  description?: string;
  input_schema?: Record<string, any>;
}

export interface MCPServer {
  mcp_server_id: number;
  project_id: number | null;  // CHANGED: nullable for user-level servers (AC-785)
  user_id: number;            // ADDED: user who owns this server (AC-785)
  scope: 'user' | 'project' | 'built_in';  // ADDED: scope type (AC-785), built_in for system-provided servers
  name: string;
  description?: string;
  transport_type: MCPTransportType;
  stdio_config?: MCPServerStdioConfig;
  sse_config?: MCPServerSSEConfig;
  // AC-1738: present when transport_type === 'streamable-http' (assistant-
  // provisioned remote servers).
  streamable_http_config?: MCPServerStreamableHttpConfig;
  env_var_names: string[];
  cached_tools?: MCPToolInfo[];
  tools_cached_at?: string;
  health_status: MCPHealthStatus;
  last_health_check?: string;
  last_error?: string;
  created_at: string;
  updated_at?: string;
}

export interface MCPServerCreate {
  name: string;
  description?: string;
  transport_type: MCPTransportType;
  stdio_config?: MCPServerStdioConfig;
  sse_config?: MCPServerSSEConfig;
  // AC-1801: present when transport_type === 'streamable-http' so a
  // provisioned remote server can be hand-edited through the same modal.
  streamable_http_config?: MCPServerStreamableHttpConfig;
  env_vars?: { name: string; value: string }[];
}

export interface MCPServerUpdate {
  name?: string;
  description?: string;
  stdio_config?: MCPServerStdioConfig;
  sse_config?: MCPServerSSEConfig;
  // AC-1801: editing a provisioned streamable-http server round-trips its
  // URL/headers config through the update payload.
  streamable_http_config?: MCPServerStreamableHttpConfig;
  env_vars?: { name: string; value: string }[];
}

export interface MCPServerTemplateEnvVar {
  name: string;
  description: string;
  example?: string;
  help_text?: string;
}

export interface MCPServerTemplate {
  id: string;
  name: string;
  description: string;
  transport_type: MCPTransportType;
  stdio_config?: MCPServerStdioConfig;
  required_env_vars: MCPServerTemplateEnvVar[];
  expected_tools?: string[];
  setup_docs_url?: string;
}

export interface MCPTestConnectionResponse {
  success: boolean;
  message: string;
  server_info?: Record<string, any>;
}

/**
 * Response for listing all available MCP servers in a project context.
 * Returns both user-level and project-level servers.
 * Technical Design: TDD-AC-784-MCP-Scoping.md Section 3.5.1
 */
export interface MCPServersAvailableResponse {
  built_in: MCPServer[];
  user_level: MCPServer[];
  project_level: MCPServer[];
}

// --- Assistant MCP Discovery / Provisioning Types (AC-1738, epic AC-1732) ---
// TDD-AC-1732 sections 3.3 + 4.6. A Tier-3 capability resolution surfaces a
// remote MCP server candidate (plus a ranked shortlist) as a pending assistant
// action that the user must explicitly approve before anything is provisioned.

/**
 * A remote MCP server candidate sourced from a public registry. The trimmed,
 * structured fields shown to the user at the approval gate (never raw HTML).
 * `registry_name` is the reverse-DNS id and the dedup key.
 */
export interface McpServerCandidate {
  registry_name: string;
  display_name: string;
  description?: string;
  remote_url?: string;
  transport?: string;
  repository_url?: string;
  is_official: boolean;
  publisher?: string;
  popularity?: number;
  requires_auth: boolean;
}

/**
 * A Tier-3 `propose_mcp_server` preview: the top candidate plus an up-to-3
 * shortlist and an id-free "why this matched" message. Surfaced inline in the
 * assistant reply as an `McpProposalCard` (the approval gate).
 */
export interface McpProposalPreview {
  action_id: number;
  action_type: 'propose_mcp_server';
  message: string;
  candidate: McpServerCandidate;
  shortlist: McpServerCandidate[];
}

/**
 * The provision request carried on Approve. Scope defaults to project but is
 * always explicit; credentials are collected AFTER approval intent only when the
 * candidate requires auth, and are sent solely on this request (never logged).
 */
export interface McpProvisionRequest {
  scope: 'project' | 'user';
  project_id?: number;
  credentials?: { name: string; value: string }[];
  header_name?: string;
  header_value_ref?: string;
}

/**
 * AC-1746 (defect AC-1743, epic AC-1732). An in-chat setup preview for a built-in
 * tool the user has NOT configured yet (e.g. Email). When the assistant resolves
 * a capability need to a built-in whose credentials are missing, it surfaces this
 * as an `McpConfigureBuiltinCard` (the in-chat configure gate) instead of telling
 * the user to go to settings. Mirrors `McpProposalPreview` but writes a built-in
 * credential (via `BuiltinCredentialService`) rather than provisioning a server.
 */
export interface McpConfigureBuiltinPreview {
  action_id: number;
  action_type: 'configure_builtin_mcp';
  message: string;
  /** Built-in server id, e.g. -2 for the Email built-in. */
  builtin_server_id: number;
  /** Human label of the built-in being configured, e.g. "Email". */
  server_name: string;
  /** Env var names the built-in needs, for display. */
  required_credentials: string[];
}

/**
 * The configure request carried on Connect. `credentials` is a
 * `SaveCredentialRequest`-shaped payload (provider, email, app_password, plus
 * host/port auto-filled from the chosen preset) that is reused verbatim by
 * `BuiltinCredentialService.save_credentials`. Collected only after the user
 * sees the configure intent; never logged.
 */
export interface McpConfigureBuiltinRequest {
  builtin_server_id: number;
  credentials: Record<string, any>;
}

/**
 * AC-1790 (epic AC-1787). The backend's SUGGESTED cadence for the just-built
 * email-summary workflow, parsed from the user's goal phrasing (a SUGGESTION the
 * card lets the user adjust). Mirrors the cron-preset / interval vocabulary of
 * `ScheduleConfigPanel`. For a cron suggestion: `schedule_type: 'cron'` with a
 * `cron_preset` (daily/weekly/...) + `cron_time` (HH:MM) and, for weekly, a
 * `day_of_week`. For an interval suggestion: `schedule_type: 'interval'` with
 * `interval_minutes`.
 */
export interface SuggestedSchedule {
  schedule_type: 'cron' | 'interval';
  cron_preset?: 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'hourly';
  cron_time?: string; // 'HH:MM'
  day_of_week?: number; // 0-6 (Mon=1), for the weekly preset
  interval_minutes?: number;
}

/**
 * AC-1790 (epic AC-1787). An in-chat preview that drives schedule setup as the
 * FINAL concierge step: when the user's email-summary goal implies recurrence
 * ("summarize my emails every morning"), the journey surfaces this so the user
 * can confirm a recurring schedule for the just-built workflow WITHOUT leaving
 * chat. Mirrors `McpConfigureBuiltinPreview` (single `configure_schedule` action
 * type, not a preview/confirm/decline triple) but writes an OrchestrationSchedule
 * via `OrchestrationScheduleService.create_schedule` rather than a credential.
 */
export interface ScheduleConfirmPreview {
  action_id: number;
  action_type: 'configure_schedule';
  message: string;
  /** The just-built email-summary workflow this schedule will run. */
  orchestration_id: number;
  /** Human label of the workflow being scheduled. */
  workflow_name: string;
  /** Backend's parsed cadence suggestion; the card prefills + lets the user adjust. */
  suggested_schedule: SuggestedSchedule;
}

/**
 * AC-1790 (epic AC-1787). The schedule request carried on Confirm. The card
 * builds the final cadence from the preset+time (cron) OR the interval, defaults
 * the timezone to the browser's IANA zone, and always enables the schedule. The
 * backend takes `orchestration_id` from the stored action_data (authoritative);
 * this field is sent for parity but is NOT trusted server-side.
 */
export interface ScheduleConfirmRequest {
  orchestration_id: number;
  schedule_type: 'cron' | 'interval';
  cron_expression?: string;
  interval_minutes?: number;
  timezone?: string;
  is_enabled: boolean;
}

// --- Social / Sharing Types (AC-856) ---

export interface SharedProjectCard {
  project_id: number;
  name: string;
  description: string | null;
  owner_username: string;
  owner_user_id: number;
  view_count: number;
  created_at: string | null;
  updated_at: string | null;
  // Phase 2: Version info for feed display
  latest_version_name?: string;
  latest_version_published_at?: string;
  event_type?: string; // "new_project" or "new_version"
}

export interface SharedProjectDetail {
  project_id: number;
  name: string;
  description: string | null;
  owner_username: string;
  owner_user_id: number;
  view_count: number;
  agent_count: number;
  orchestration_count: number;
  model_count: number;
  created_at: string | null;
}

export interface UserPublicProfile {
  user_id: number;
  username: string;
  public_project_count: number;
  follower_count: number;
  following_count: number;
  is_following: boolean;
  created_at: string | null;
}

export interface UserPublicSummary {
  user_id: number;
  username: string;
  public_project_count: number;
  follower_count: number;
  is_following: boolean;
}

export interface UserListResponse {
  users: UserPublicSummary[];
  total: number;
}

export interface SharedProjectListResponse {
  projects: SharedProjectCard[];
  total: number;
}

export interface FollowResponse {
  follower_id: number;
  following_id: number;
  created_at: string | null;
}

export interface SharedToolSummary {
  tool_id: number;
  name: string | null;
  type: string;
  description: string | null;
}

export interface SharedAgentSummary {
  agent_id: number;
  name: string;
  instructions: string | null;
  handoff_description: string | null;
  model: string | null;
  model_settings: Record<string, any> | null;
  output_type: string | null;
  tools: SharedToolSummary[];
  created_at: string | null;
}

export interface SharedWorkflowSummary {
  orchestration_id: number;
  name: string;
  description: string | null;
  step_count: number;
  created_at: string | null;
}

export interface SharedWorkflowDetail {
  orchestration_id: number;
  name: string;
  description: string | null;
  start_node_position_x: number | null;
  start_node_position_y: number | null;
  steps: SharedWorkflowStepDetail[];
  created_at: string | null;
}

export interface SharedWorkflowStepDetail {
  orchestration_step_id: number;
  lineage_id?: string | null;
  step_type: string | null;
  intent: string | null;
  agent_id: number | null;
  description: string | null;
  label: string | null;
  input_message: string | null;
  condition: Record<string, any> | null;
  on_true: Record<string, any> | null;
  on_false: Record<string, any> | null;
  previous_step_id: number | null;
  output_step_id: number | null;
  output_action: string | null;
  position_x: number | null;
  position_y: number | null;
  execution_mode: string | null;
  fan_out: boolean | null;
}

export interface SharedModelSummary {
  model_id: number;
  class_name: string;
  description: string | null;
  properties: Record<string, string> | null;
  relations: Record<string, string> | null;
  created_at: string | null;
}

// --- Team Types (AC-888, Phase 3) ---

export interface TeamCreate {
  name: string;
  description?: string;
}

export interface TeamUpdate {
  name?: string;
  description?: string;
}

export interface TeamResponse {
  team_id: number;
  name: string;
  description: string | null;
  owner_id: number;
  owner_username: string;
  member_count: number;
  project_count: number;
  created_at: string;
}

export interface TeamDetail extends TeamResponse {
  owner_username: string;
  is_owner: boolean;
}

export interface TeamMember {
  user_id: number;
  username: string;
  role: string;
  joined_at: string;
}

export interface TeamInvitation {
  invitation_id: number;
  team_id: number;
  team_name: string;
  inviter_username: string;
  invitee_username: string;
  status: string;
  created_at: string;
}

export interface TeamListResponse {
  teams: TeamResponse[];
  total: number;
}

export interface TeamMemberListResponse {
  members: TeamMember[];
}

export interface TeamInvitationListResponse {
  invitations: TeamInvitation[];
  total: number;
}

// --- Phase 2 Social / Sharing Types (AC-856) ---

export interface PublishVersionRequest {
  version_name: string;
  description?: string;
}

export interface PublishedVersionSummary {
  checkpoint_id: number;
  project_id: number;
  version_name: string;
  description: string | null;
  entity_counts: Record<string, number> | null;
  published_at: string | null;
}

export interface PublishedVersionListResponse {
  versions: PublishedVersionSummary[];
  total: number;
}

export interface PublishedVersionDetail extends PublishedVersionSummary {
  snapshot: {
    orchestrations: SharedWorkflowSnapshotItem[];
    agents: SharedAgentSnapshotItem[];
    models: SharedModelSnapshotItem[];
  };
}

export interface SharedWorkflowSnapshotItem {
  orchestration_id: number;
  name: string;
  description: string | null;
  steps: SharedWorkflowStepDetail[];
}

export interface SharedAgentSnapshotItem {
  agent_id: number;
  name: string;
  instructions: string | null;
  handoff_description: string | null;
  model: string | null;
  model_settings: Record<string, any> | null;
  output_type: string | null;
  tools: SharedToolSummary[];
}

export interface SharedModelSnapshotItem {
  model_id: number;
  class_name: string;
  description: string | null;
  properties: Record<string, string> | null;
}

export interface SharedRunRequest {
  input_message?: string;
}

export interface SharedRunValidationError {
  missing_tools: string[];
  message: string;
}

// --- Admin / Allowlist Types (AC-915) ---

export interface AllowlistEntry {
  id: number;
  email: string;
  added_by_user_id: number;
  added_by_username?: string;
  created_at: string;
  is_registered?: boolean;
}

export interface AdminUserSummary {
  user_id: number;
  username: string;
  email: string;
  is_admin: boolean;
  created_at?: string;
}

// --- WhatsApp Integration Types (AC-1050) ---

export interface WhatsAppStatus {
  connected: boolean;
  phone_number: string | null;
  connected_at: string | null;
  jid: string | null;
  service_available?: boolean;
}

export interface WhatsAppConnectionResponse {
  status: 'qr_pending' | 'connected' | 'connecting';
  qr_data: string | null;
}

export interface WhatsAppQRResponse {
  qr_data: string | null;
  expires_in: number | null;
}

export interface WhatsAppPairingCodeResponse {
  pairing_code: string;
}

export interface WhatsAppNotificationPreferences {
  enabled: boolean;
  notify_on_completion: boolean;
  notify_on_failure: boolean;
  orchestration_overrides: Record<string, boolean>;
}

export interface WhatsAppNotificationPreferencesUpdate {
  enabled?: boolean;
  notify_on_completion?: boolean;
  notify_on_failure?: boolean;
}

// --- Slack Inbound ChatOps Types (Epic AC-1819 / AC-1823) ---

export interface SlackLinkStartResponse {
  code: string;
  instructions: string;
}

export interface SlackLinkStatusResponse {
  linked: boolean;
}

export interface SlackConfigResponse {
  configured: boolean;
  team_id: string | null;
}

// --- Built-in Email / MCP Credential Types (AC-1108) ---

export interface BuiltinCredentialStatus {
  has_credentials: boolean;
  credential_id?: number | null;
  provider: string | null;
  instance_label?: string | null;
  email: string | null;
  is_active: boolean;
  last_tested_at: string | null;
  last_test_success: boolean | null;
  created_at: string | null;
}

export interface EmailProviderPreset {
  id: string;
  name: string;
  imap_host: string;
  imap_port: number;
  smtp_host: string;
  smtp_port: number;
  help_url: string;
  help_steps: string[];
  /**
   * One-line note (AC-1759) explaining why the regular account password won't
   * work and an app password is required. Surfaced by McpConfigureBuiltinCard's
   * "How do I get an app password?" disclosure.
   */
  why: string;
}

export interface SaveCredentialRequest {
  provider: string;
  instance_label?: string;
  email: string;
  app_password: string;
  imap_host: string;
  imap_port: number;
  smtp_host: string;
  smtp_port: number;
}

/**
 * AC-1807 (epic AC-1803). The single-source-of-truth Slack guided-setup preset
 * served by GET /api/v1/mcp/slack-setup. It drives the Slack branch of
 * McpConfigureBuiltinCard: the token label, the `xoxb-` prefix used for the
 * client-side Connect gate, the help URL/steps/why note, and the exact env var
 * the curated Slack built-in server reads. Mirrors EmailProviderPreset but for a
 * single-token paste flow rather than a host/port provider select.
 */
export interface SlackSetupPreset {
  id: string;
  name: string;
  /** Visible label for the secret input, e.g. "Bot User OAuth Token". */
  token_label: string;
  /** Prefix a valid Slack bot token starts with, e.g. "xoxb-". Used to gate Connect. */
  token_prefix: string;
  /** Exact env var the curated server reads, e.g. "SLACK_BOT_TOKEN" (display only). */
  env_var: string;
  /** Slack app management page, opened in a new tab from the help disclosure. */
  help_url: string;
  /** One-line note explaining why a bot token (not a login/password) is required. */
  why: string;
  /** Ordered guided-setup steps (create app, add scopes, install, copy token). */
  help_steps: string[];
}

export interface TestConnectionResult {
  success: boolean;
  imap_ok?: boolean;
  smtp_ok?: boolean;
  message: string;
}

// Instruction Assistant types (AC-1189)
export interface InstructionGenerateRequest {
  agent_name: string;
  project_id?: number;
  model: string;
  intent?: 'create' | 'enhance' | 'validate' | 'assistant';
  input_schema?: { class_name: string; properties: Record<string, string>; description?: string };
}

export interface InstructionImproveRequest {
  agent_name: string;
  current_instructions: string;
  project_id?: number;
  model: string;
  intent?: 'create' | 'enhance' | 'validate' | 'assistant';
  input_schema?: { class_name: string; properties: Record<string, string>; description?: string };
}

export interface InstructionRefineRequest {
  agent_name: string;
  current_instructions: string;
  message: string;
  conversation_history: Array<{ role: 'user' | 'assistant'; content: string }>;
  project_id?: number;
  model: string;
  intent?: 'create' | 'enhance' | 'validate' | 'assistant';
  input_schema?: { class_name: string; properties: Record<string, string>; description?: string };
}

export interface SuggestedSchema {
  class_name: string;
  description?: string;
  properties: Record<string, string>;
}

export interface SuggestedTool {
  type: 'web_search' | 'knowledge_search';
  rationale?: string;
}

export interface InstructionAssistantResponse {
  instructions: string;
  model_used: string;
  message?: string;
  improvements_summary?: string;
  suggested_schema?: SuggestedSchema[] | null;
  suggested_tools?: SuggestedTool[] | null;
}

// LLM Catalog (AC-1347, extended in AC-1493 P6 for BYOK)
export type LLMTier = 'light' | 'standard' | 'premium' | 'reasoning';

/**
 * Provider identifier used by BYOK (AC-1493). The backend catalog and
 * user-credentials API use these stable strings.
 */
export type LLMProvider = 'openai' | 'anthropic' | 'google';

export interface LLMCatalogEntry {
  id: string;
  label: string;
  tier: LLMTier;
  supports_structured?: boolean;
  reasoning?: boolean;
  /**
   * AC-1493 P6 extended catalog fields. Optional on the type so legacy
   * fixtures and tests that pre-date the BYOK epic still type-check; the
   * live backend always returns these. Consumers that need the BYOK
   * gating behavior (AgentFormModal picker, OrchestrationEditor banner)
   * must defensive-default when reading them.
   */
  provider?: LLMProvider;
  supports_tools?: boolean;
  supports_vision?: boolean;
  context_window?: number;
  input_price_per_1m?: number;
  output_price_per_1m?: number;
  description?: string;
  /**
   * Whether the model is currently runnable for this user. In BYOK mode
   * this is `false` when the user has not configured a key for the
   * provider; in non-BYOK mode the backend reports it as `true`.
   */
  available?: boolean;
}

export interface LLMCatalogResponse {
  /**
   * AC-1493 P6: when true, the deployment is in BYOK mode and per-model
   * `available` flags reflect whether the current user has the necessary
   * provider key. When false (server-key mode), all configured providers
   * are available.
   */
  byok_mode?: boolean;
  models: LLMCatalogEntry[];
}

/**
 * AC-1616 (epic AC-1609): The key-gated assistant model allow-list returned by
 * `GET /api/v1/assistant/models`. Only mid-tier+ models the deployment can run
 * (server key or user BYOK key present) appear in `models`; `default` is the
 * id the assistant falls back to when the user has no saved preference.
 */
export interface AssistantModelsResponse {
  models: LLMCatalogEntry[];
  default: string;
}

/**
 * AC-1616: The current user's saved assistant model preference returned by
 * `GET /api/v1/assistant/model-preference`. `model` is `null` when the user
 * has not chosen one (the UI then falls back to the allow-list `default`).
 */
export interface AssistantModelPreference {
  model: string | null;
}

// BYOK user-credentials API (AC-1493)
export interface UserApiKey {
  provider: LLMProvider;
  has_key: boolean;
  display_label: string | null;
  last_validated_at: string | null;
  created_at: string | null;
  /**
   * AC-1619: TRUE only when the deployment is NOT in BYOK mode AND the
   * provider's platform env key is present (OPENAI_API_KEY for openai,
   * ANTHROPIC_API_KEY for anthropic; google has none -> always false). In
   * BYOK mode this is always false. Independent of `has_key`, which still
   * means "this user has a per-user DB row for this provider".
   */
  platform_configured?: boolean;
}

/**
 * AC-1619: Deployment-level runtime mode, returned by
 * `GET /api/v1/system/mode`. `byok_mode` is TRUE when per-user (BYOK) keys
 * are honored by the runtime, FALSE in "Friends & Family" / platform-managed
 * mode (per-user keys ignored; platform env keys used instead).
 * `supported_providers` is the canonical list of provider ids the deployment
 * knows about.
 */
export interface SystemMode {
  byok_mode: boolean;
  supported_providers: string[];
}

export interface CreateApiKeyRequest {
  api_key: string;
  display_label?: string | null;
}

export interface ApiKeyTestResult {
  provider: LLMProvider;
  ok: boolean;
  error?: string | null;
  last_validated_at?: string | null;
}

// Export analytics types
export * from './analytics';

// Export token usage types (AC-604)
export * from './tokenUsage';
